// @ts-nocheck
import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { FileText, AlertTriangle, CheckCircle, XCircle, Scale, Mail } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';

const Terms: React.FC = () => {
  const { isDark } = useTheme();

  const sections = [
    {
      icon: CheckCircle,
      title: 'Acceptance of Terms',
      content: [
        {
          text: 'By accessing and using InvestVerse.AI, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform.'
        },
        {
          text: 'We reserve the right to modify these terms at any time. Continued use of the platform after changes constitutes acceptance of the modified terms.'
        }
      ]
    },
    {
      icon: FileText,
      title: 'Service Description',
      content: [
        {
          subtitle: 'AI-Powered Analysis',
          text: 'InvestVerse.AI provides AI-powered cryptocurrency market analysis, technical indicators, sentiment analysis, and trading insights. Our platform uses advanced algorithms and real-time data to generate reports and recommendations.'
        },
        {
          subtitle: 'No Investment Advice',
          text: 'Our services are for informational and educational purposes only. We do not provide investment, financial, legal, or tax advice. All analysis and recommendations should be considered as general information, not personalized advice.'
        },
        {
          subtitle: 'User Responsibility',
          text: 'You are solely responsible for your investment decisions. Cryptocurrency trading involves substantial risk of loss, and you should only invest what you can afford to lose.'
        }
      ]
    },
    {
      icon: AlertTriangle,
      title: 'Risk Disclosure',
      content: [
        {
          subtitle: 'Market Volatility',
          text: 'Cryptocurrency markets are highly volatile and unpredictable. Past performance does not guarantee future results. Prices can fluctuate significantly in short periods.'
        },
        {
          subtitle: 'No Guarantees',
          text: 'We make no guarantees about the accuracy, completeness, or timeliness of our analysis. Our AI-powered insights are based on available data and algorithms that may not capture all market factors.'
        },
        {
          subtitle: 'Financial Loss',
          text: 'Trading cryptocurrencies carries a high level of risk and may result in the loss of all your invested capital. You should carefully consider whether trading is appropriate for you based on your financial situation.'
        }
      ]
    },
    {
      icon: Scale,
      title: 'User Obligations',
      content: [
        {
          subtitle: 'Account Security',
          text: 'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately of any unauthorized access.'
        },
        {
          subtitle: 'Accurate Information',
          text: 'You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate and current.'
        },
        {
          subtitle: 'Prohibited Activities',
          text: 'You may not use our platform for any illegal purposes, to violate any laws, to harass others, to distribute malware, to attempt unauthorized access to our systems, or to interfere with the proper functioning of the platform.'
        },
        {
          subtitle: 'Compliance',
          text: 'You are responsible for compliance with all applicable laws and regulations in your jurisdiction, including tax obligations related to cryptocurrency trading.'
        }
      ]
    },
    {
      icon: XCircle,
      title: 'Limitations of Liability',
      content: [
        {
          subtitle: 'Disclaimer of Warranties',
          text: 'Our platform is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.'
        },
        {
          subtitle: 'Limitation of Damages',
          text: 'To the maximum extent permitted by law, InvestVerse.AI shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.'
        },
        {
          subtitle: 'Third-Party Services',
          text: 'We are not responsible for the content, accuracy, or availability of third-party services or data sources we may integrate with or reference.'
        }
      ]
    },
    {
      icon: FileText,
      title: 'Intellectual Property',
      content: [
        {
          subtitle: 'Platform Ownership',
          text: 'All content, features, and functionality of InvestVerse.AI, including but not limited to text, graphics, logos, icons, images, audio clips, data compilations, and software, are owned by us or our licensors and are protected by copyright, trademark, and other intellectual property laws.'
        },
        {
          subtitle: 'Limited License',
          text: 'We grant you a limited, non-exclusive, non-transferable license to access and use our platform for your personal, non-commercial use, subject to these terms.'
        },
        {
          subtitle: 'Restrictions',
          text: 'You may not reproduce, distribute, modify, create derivative works of, publicly display, or exploit any part of our platform without our express written permission.'
        }
      ]
    },
    {
      icon: XCircle,
      title: 'Termination',
      content: [
        {
          subtitle: 'By You',
          text: 'You may terminate your account at any time by contacting us or using the account deletion feature in your settings.'
        },
        {
          subtitle: 'By Us',
          text: 'We reserve the right to suspend or terminate your access to our platform at any time, with or without notice, for any reason, including but not limited to violation of these terms, fraudulent activity, or abuse of our services.'
        },
        {
          subtitle: 'Effect of Termination',
          text: 'Upon termination, your right to use the platform will immediately cease. Provisions that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.'
        }
      ]
    },
    {
      icon: Scale,
      title: 'Governing Law and Disputes',
      content: [
        {
          subtitle: 'Governing Law',
          text: 'These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which InvestVerse.AI operates, without regard to its conflict of law provisions.'
        },
        {
          subtitle: 'Dispute Resolution',
          text: 'Any disputes arising out of or relating to these Terms or the use of our platform shall be resolved through good faith negotiations. If negotiations fail, disputes may be subject to binding arbitration or litigation as specified by applicable law.'
        }
      ]
    }
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-gray-50'}`}>
      {/* Header */}
      <PublicHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
            isDark
              ? 'bg-blue-500/20 border border-blue-500/30'
              : 'bg-blue-50 border border-blue-200'
          }`}>
            <FileText className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
              Terms of Service
            </span>
          </div>

          <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Terms of Service
          </h1>

          <p className={`text-lg ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Introduction */}
        <div className={`p-6 rounded-2xl border mb-8 ${
          isDark
            ? 'bg-white/5 border-white/10'
            : 'bg-white border-gray-200'
        }`}>
          <p className={`text-base leading-relaxed ${
            isDark ? 'text-zinc-300' : 'text-gray-700'
          }`}>
            Welcome to InvestVerse.AI. These Terms of Service ("Terms") govern your access to and use of our AI-powered
            cryptocurrency analysis platform. Please read these Terms carefully before using our services. By using
            InvestVerse.AI, you acknowledge that you have read, understood, and agree to be bound by these Terms.
          </p>
        </div>

        {/* Important Notice */}
        <div className={`p-6 rounded-2xl border mb-8 ${
          isDark
            ? 'bg-red-500/10 border-red-500/20'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-6 h-6 flex-shrink-0 mt-0.5 ${
              isDark ? 'text-red-400' : 'text-red-600'
            }`} />
            <div>
              <h3 className={`text-lg font-semibold mb-2 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Important Notice
              </h3>
              <p className={`text-base leading-relaxed ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>
                Cryptocurrency trading involves substantial risk of loss. Our platform provides analysis and insights
                for informational purposes only and should not be considered financial advice. Always conduct your own
                research and consult with qualified financial advisors before making investment decisions.
              </p>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;

            return (
              <div
                key={index}
                className={`p-8 rounded-2xl border ${
                  isDark
                    ? 'bg-white/5 border-white/10'
                    : 'bg-white border-gray-200'
                }`}
              >
                {/* Section Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                    isDark ? 'bg-blue-500/10' : 'bg-blue-50'
                  }`}>
                    <Icon className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <h2 className={`text-2xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {section.title}
                  </h2>
                </div>

                {/* Section Content */}
                <div className="space-y-6 ml-16">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      {item.subtitle && (
                        <h3 className={`text-lg font-semibold mb-2 ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {item.subtitle}
                        </h3>
                      )}
                      <p className={`text-base leading-relaxed ${
                        isDark ? 'text-zinc-400' : 'text-gray-600'
                      }`}>
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact Section */}
        <div className={`mt-8 p-8 rounded-2xl border text-center ${
          isDark
            ? 'bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/20'
            : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
        }`}>
          <Mail className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <h3 className={`text-xl font-bold mb-2 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Questions About These Terms?
          </h3>
          <p className={`text-base mb-4 ${
            isDark ? 'text-zinc-400' : 'text-gray-600'
          }`}>
            If you have any questions or concerns about these Terms of Service, please contact us.
          </p>
          <a
            href="mailto:legal@investverse.ai"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
              isDark
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <Mail className="w-4 h-4" />
            Contact Legal Team
          </a>
        </div>

        {/* Acknowledgment */}
        <div className={`mt-8 p-4 rounded-xl border text-sm ${
          isDark
            ? 'bg-white/5 border-white/10 text-zinc-400'
            : 'bg-gray-50 border-gray-200 text-gray-600'
        }`}>
          <p>
            <strong className={isDark ? 'text-white' : 'text-gray-900'}>By using InvestVerse.AI, you acknowledge that:</strong>
            {' '}You have read and understood these Terms of Service, you agree to be bound by them, you understand the
            risks associated with cryptocurrency trading, and you accept full responsibility for your investment decisions.
          </p>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Terms;
